import React from 'react';

export const VERIFIED_DOMAINS = ['ellesho.me', 'elle.email'] as const;

export const isVerifiedDomain = (domain: string): boolean => {
  return VERIFIED_DOMAINS.some(verifiedDomain => domain.includes(verifiedDomain));
};

export const isLightColor = (color: string): boolean => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 200;
};

export const getContrastingTextColor = (backgroundColor: string): string => {
  return isLightColor(backgroundColor) ? "black" : "white";
};

export const sharedStyles = {
  domainNameContainer: {
    position: 'absolute' as const,
    top: '-50px',
    left: '12px',
    fontSize: '10px',
    padding: '4px 4px 4px 5px', 
    borderRadius: '20px',
    whiteSpace: 'nowrap' as const,
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  },
  verificationIcon: {
    marginLeft: '4px',
    width: '14px',
    height: '14px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative' as const,
  },
  verificationCheckmark: {
    position: 'absolute' as const,
    fontSize: '10px',
    fontWeight: 'bold',
  },
  simpleVerificationIcon: {
    marginLeft: '4px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '8px',
    fontWeight: 'bold',
  },
  chatBubble: {
    position: 'absolute' as const,
    fontSize: '16px',
    fontStyle: 'normal' as const,
    fontFamily: 'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    lineHeight: 1,
    padding: '8px 16px',
    borderRadius: '24px',
    whiteSpace: 'nowrap' as const,
    top: '-24px',
    left: '12px',
    boxShadow: '0 2px 2px rgba(0,0,0,0.2)',
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    gap: '8px',
    minWidth: '2.5em',
    boxSizing: 'border-box' as const,
    zIndex: 300,
    pointerEvents: 'none' as const,
  },
  textarea: {
    width: '100%',
    height: '80px',
    padding: '14px',
    fontFamily: 'monospace',
    fontSize: '0.8em',
    background: 'rgba(255, 255, 255, 0.7)',
    boxSizing: 'border-box' as const,
    resize: 'none' as const,
    border: '1px solid #fff',
    outline: 'none',
    borderRadius: '8px',
  },
  modalButton: {
    fontFamily: 'sans-serif',
    padding: '14px 30px',
    borderRadius: '26px',
    border: '1px solid #fff',
    background: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    userSelect: 'none', 
    WebkitUserSelect: 'none',
  },
  button: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '1px solid white',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    boxSizing: 'border-box',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    outline: 'none',
  },
  controlButton: {
    backgroundColor: 'rgba(245, 245, 245, 0.7)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  colorPickerContainer: {
    width: '44px',
    height: '44px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  colorButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '1px solid white',
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
  },
  colorInput: {
    position: 'absolute',
    inset: '-5px',
    width: 'calc(100% + 10px)',
    height: 'calc(100% + 10px)',
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
  },
  cursorWrapper: {
    transform: 'translateY(6px)',
  },
} as const;

export const verificationIconStyles = {
  verificationIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '4px',
    position: 'relative' as const,
  },
  verificationCheckmark: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '8px',
    fontWeight: 'bold',
  },
  simpleVerificationIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '4px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    fontSize: '8px',
    fontWeight: 'bold',
  },
} as const;

interface VerificationIconProps {
  isLight: boolean;
  color: string;
}

export const VerificationIcon: React.FC<VerificationIconProps> = React.memo(({ isLight, color }) => (
  <span style={verificationIconStyles.verificationIcon}>
    <svg viewBox="0 0 294.996 294.996" width="14" height="14">
      <path d="M280.977,118.478c-13.619-10.807-20.563-27.57-18.574-44.845c1.3-11.3-2.566-22.393-10.607-30.432
      c-8.044-8.043-19.136-11.909-30.434-10.607c-17.281,1.986-34.037-4.954-44.844-18.573C169.449,5.11,158.872,0,147.499,0
      c-11.374,0-21.951,5.11-29.021,14.02c-10.807,13.618-27.564,20.56-44.841,18.575c-11.3-1.305-22.393,2.563-30.435,10.605
      c-8.043,8.04-11.909,19.133-10.609,30.435c1.989,17.272-4.954,34.035-18.576,44.844C5.11,125.549,0,136.126,0,147.498
      s5.109,21.949,14.019,29.021c13.62,10.808,20.563,27.57,18.574,44.845c-1.3,11.3,2.566,22.393,10.607,30.432
      c8.044,8.043,19.145,11.911,30.434,10.607c17.274-1.988,34.037,4.954,44.844,18.573c7.069,8.91,17.646,14.021,29.021,14.021
      c11.373,0,21.95-5.11,29.02-14.02c10.808-13.618,27.565-20.559,44.841-18.575c11.301,1.299,22.393-2.563,30.435-10.605
      c8.043-8.04,11.909-19.133,10.609-30.434c-1.989-17.273,4.955-34.037,18.576-44.845c8.907-7.07,14.017-17.647,14.017-29.02
      S289.886,125.549,280.977,118.478z" fill={isLight ? "black" : "white"} />
    </svg>
    <span style={{
      ...verificationIconStyles.verificationCheckmark,
      color: color,
    }}>
      ✓
    </span>
  </span>
));

export const SimpleVerificationIcon: React.FC<VerificationIconProps> = React.memo(({ isLight, color }) => (
  <span style={{
    ...verificationIconStyles.simpleVerificationIcon,
    backgroundColor: isLight ? "black" : "white",
    color: color,
  }}>
    ✓
  </span>
)); 